import { FC } from 'react'

import { useTranslation } from 'react-i18next'

import clsx from 'clsx'

import {
  TourDocumentView,
  formatPaymentAmount,
  useAdditionalTourPayment,
} from 'entities/tour/models/document'

import { Button } from 'shared/ui/button'

import s from './styles.module.scss'

interface Props {
  document: TourDocumentView
  hasRefreshError: boolean
  onPayPart: () => void
}

export const PaymentPanel: FC<Props> = ({
  document,
  hasRefreshError,
  onPayPart,
}) => {
  const { t } = useTranslation()
  const { pay, refresh, isPaying, isRefreshing, isBlocked } =
    useAdditionalTourPayment(document, hasRefreshError)

  if (document.remainingAmount === null || document.remainingAmount <= 0) {
    return null
  }

  return (
    <aside
      className={s.panel}
      aria-label={t('tourDetails.remainingPayment')}
    >
      <div className={s.summary}>
        <div className={clsx(s.row, 'semi-bold-16')}>
          <span>{t('tourDetails.remainingPayment')}</span>
          <span>
            {formatPaymentAmount(document.remainingAmountRaw)}
            <u>с</u>
          </span>
        </div>
        <div className={clsx(s.row, s.total, 'medium-14')}>
          <span>{t('tourDetails.price')}</span>
          {document.amount !== null && (
            <span>
              {document.amount.toLocaleString('ru-RU')}
              <u>с</u>
            </span>
          )}
        </div>
      </div>
      <div className={s.divider} />
      <div className={s.actions}>
        {hasRefreshError ? (
          <>
            <p
              className="medium-14"
              role="status"
            >
              {t('tourAdditionalPayment.refreshFailed')}
            </p>
            <Button
              type="button"
              variant="grey"
              loading={isRefreshing}
              disabled={isPaying || isRefreshing}
              onClick={() => void refresh()}
            >
              {t('tourAdditionalPayment.refresh')}
            </Button>
          </>
        ) : (
          <>
            <Button
              type="button"
              className={clsx(s.action, 'semi-bold-17')}
              loading={isPaying}
              disabled={isBlocked}
              onClick={() => void pay()}
            >
              {t('tourDetails.payFull')}
            </Button>
            <Button
              variant="grey"
              className={clsx(s.action, 'semi-bold-17')}
              type="button"
              disabled={isBlocked}
              onClick={onPayPart}
            >
              {t('tourDetails.payPart')}
            </Button>
          </>
        )}
      </div>
    </aside>
  )
}
